import T from 'prop-types';
import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Button, Box } from '@acast/decibel';
import Episode, {
  publishEpisode,
  unpublishEpisodeWithConfirmation,
  isEpisodeBeingPublished,
} from 'resources/episode';
import resourceProp from 'shared/helpers/resource-prop';
import {
  saveEpisodeAccessSettings as saveEpisodeAccessSettingsAction,
  selectEpisodeAccessHasChanged,
} from 'ducks/access';

const PublishControls = ({
  episode,
  canPublish,
  isUpdatingEpisode,
  hasLocalAccessChanges,
  saveEpisodeAccessSettings,
}) => {
  if (episode.pending) {
    return null;
  }

  const {
    id: episodeId,
    show: showId,
    isModified,
    isPublished,
    isQueued,
    publishDate,
  } = episode.data;

  /* eslint-disable react-hooks/rules-of-hooks */
  const showUpdateButton = React.useMemo(
    () => ((isPublished || isQueued) && isModified) || (isPublished && hasLocalAccessChanges),
    [isPublished, isQueued, isModified, hasLocalAccessChanges]
  );
  const showUnpublishButton = React.useMemo(() => isPublished || isQueued, [isPublished, isQueued]);
  const showPublishButton = React.useMemo(() => !showUnpublishButton, [showUnpublishButton]);
  const hasValidScheduledDate = React.useMemo(() => moment(publishDate).isValid(), [publishDate]);
  /* eslint-enable react-hooks/rules-of-hooks */

  const isScheduledForFuture = moment(publishDate).isAfter(Date.now());

  const handlePublish = () => {
    const isDraft = !isPublished && !isQueued;
    const hasChanges = (isPublished || isQueued) && isModified;

    if (isDraft || hasChanges) {
      publishEpisode(episode.data, episode.data.publishDate);
    }

    if (hasLocalAccessChanges) {
      saveEpisodeAccessSettings({ showId, episodeId });
    }
  };

  const handleUnpublish = () => {
    unpublishEpisodeWithConfirmation(episode.data);
  };

  return (
    <Box gap={4} justifyContent="flex-end">
      {showUnpublishButton && (
        <Button
          data-test-id="PublishControls__UnpublishButton"
          variant="primary"
          intent="danger"
          busy={isUpdatingEpisode}
          onClick={handleUnpublish}
        >
          Unpublish
        </Button>
      )}
      {showUpdateButton && (
        <Button
          variant="primary"
          data-test-id="PublishControls__UpdateButton"
          busy={isUpdatingEpisode}
          disabled={!canPublish}
          onClick={handlePublish}
        >
          {isQueued ? 'Update' : 'Publish Changes'}
        </Button>
      )}
      {showPublishButton && (
        <Button
          data-test-id="PublishControls__PublishButton"
          variant="primary"
          disabled={!canPublish || !hasValidScheduledDate}
          busy={isUpdatingEpisode}
          onClick={handlePublish}
        >
          {isScheduledForFuture ? 'Schedule Publish' : 'Publish Now'}
        </Button>
      )}
    </Box>
  );
};

PublishControls.propTypes = {
  canPublish: T.bool.isRequired,
  episode: resourceProp(Episode),
  isUpdatingEpisode: T.bool.isRequired,
  hasLocalAccessChanges: T.bool.isRequired,
  saveEpisodeAccessSettings: T.func.isRequired,
};

export const unwrapped = PublishControls;

export default connect(
  (state, props) => {
    const episodeId = props.episode.pending ? null : props.episode.data.id;
    const hasLocalAccessChanges = episodeId
      ? selectEpisodeAccessHasChanged(state, episodeId)
      : false;
    const isUpdatingEpisode = episodeId ? isEpisodeBeingPublished(episodeId) : false;

    return {
      hasLocalAccessChanges,
      isUpdatingEpisode,
    };
  },
  { saveEpisodeAccessSettings: saveEpisodeAccessSettingsAction }
)(PublishControls);
